import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaAws, FaDocker, FaServer, FaCloud, FaChartLine, FaShieldAlt } from 'react-icons/fa';
import { SiKubernetes, SiTerraform, SiJenkins, SiPrometheus, SiGooglecloud, SiGithubactions } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';

// Import DevOps specific images
import cloudHeroImg from '../../assets/cloud_devops_hero_img.png'; // Hero image
import setupMigrationImg from '../../assets/setup_and_migration_devops.png';
import automationCiCdImg from '../../assets/automation_and_ci_cd_devops.png';
import costOptimizationImg from '../../assets/cost_optimisation_devops.png';
import monitoringLoggingImg from '../../assets/monitoring_and_logging_devops.png';

const CloudDevOpsInfrastructure = () => {
  const { projects } = useSelector((state) => state.portfolio);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Cloud DevOps projects with portfolio data
  const cloudProjects = [
    {
      id: 1,
      title: 'E-COMMERCE CLOUD MIGRATION',
      subtitle: 'AWS Cloud Infrastructure',
      description: 'Successfully migrated high-traffic e-commerce platform to AWS with zero downtime, reducing costs by 40% and improving performance.',
      image: monitoringLoggingImg,
      category: 'Cloud',
      link: '/services/cloud-devops-infrastructure',
      bgGradient: 'linear-gradient(135deg, rgb(255, 152, 0) 0%, rgb(255, 193, 7) 100%)'
    },
    {
      id: 2,
      title: 'MICROSERVICES DEPLOYMENT',
      subtitle: 'Kubernetes & Docker',
      description: 'Containerized monolithic application into microservices architecture with automated CI/CD pipelines and monitoring.',
      image: automationCiCdImg,
      category: 'DevOps',
      link: '/services/cloud-devops-infrastructure',
      bgGradient: 'linear-gradient(135deg, rgb(52, 152, 219) 0%, rgb(41, 128, 185) 100%)'
    },
    {
      id: 3,
      title: 'MULTI-CLOUD SETUP',
      subtitle: 'AWS + GCP Infrastructure',
      description: 'Designed and implemented multi-cloud architecture for disaster recovery and high availability across AWS and GCP.',
      image: setupMigrationImg,
      category: 'Infrastructure',
      link: '/services/cloud-devops-infrastructure',
      bgGradient: 'linear-gradient(135deg, rgb(46, 204, 113) 0%, rgb(39, 174, 96) 100%)'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % cloudProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + cloudProjects.length) % cloudProjects.length);
  };

  const technologies = [
    { name: 'AWS', icon: <FaAws />, description: 'Amazon Web Services' },
    { name: 'GCP', icon: <SiGooglecloud />, description: 'Google Cloud Platform' },
    { name: 'Kubernetes', icon: <SiKubernetes />, description: 'Container orchestration' },
    { name: 'Terraform', icon: <SiTerraform />, description: 'Infrastructure as Code' },
    { name: 'Docker', icon: <FaDocker />, description: 'Containerization platform' },
    { name: 'Jenkins', icon: <SiJenkins />, description: 'CI/CD automation' },
    { name: 'GitHub Actions', icon: <SiGithubactions />, description: 'CI/CD workflows' },
    { name: 'Prometheus', icon: <SiPrometheus />, description: 'Monitoring & alerting' },
  ];

  const features = [
    {
      title: 'Cloud Infrastructure Setup & Migration',
      description: 'Design and deploy scalable cloud environments on AWS, Azure, or GCP. We migrate your applications safely with zero downtime and best practices.',
      image: setupMigrationImg
    },
    {
      title: 'DevOps Automation & CI/CD',
      description: 'Ship faster with automated build, test, and deployment pipelines. Includes GitHub/GitLab/Jenkins pipelines, Docker & Kubernetes.',
      image: automationCiCdImg
    },
    {
      title: 'Cloud Cost Optimization',
      description: 'Reduce your cloud bill by up to 30–50% without compromising performance. Includes cost audits, rightsizing, and storage optimization.',
      image: costOptimizationImg
    },
    {
      title: 'Monitoring, Logging & Reliability (SRE)',
      description: 'Keep your systems healthy with proactive monitoring and 24/7 alerting. Includes Prometheus/Grafana, ELK/Cloud Logging.',
      image: monitoringLoggingImg
    }
  ];

  const faqs = [
    {
      question: 'How long does cloud migration take?',
      answer: 'Cloud migration typically takes 2-8 weeks depending on application complexity. Simple applications can be migrated in 2-3 weeks, while complex enterprise systems may take 6-8 weeks. We ensure zero downtime during migration.'
    },
    {
      question: 'Which cloud platform should I choose?',
      answer: 'AWS offers the most comprehensive services, GCP excels in AI/ML and data analytics, Azure integrates well with Microsoft ecosystem. We help you choose based on your specific requirements, budget, and existing infrastructure.'
    },
    {
      question: 'How much can I save with cloud cost optimization?',
      answer: 'Most organizations can reduce cloud costs by 30-50% through rightsizing, reserved instances, spot instances, and automated scaling. We provide detailed cost analysis and optimization roadmap.'
    },
    {
      question: 'Do you provide 24/7 monitoring and support?',
      answer: 'Yes, we set up comprehensive monitoring with Prometheus, Grafana, and cloud-native tools. We provide 24/7 alerting and can offer managed support services for critical production environments.'
    },
    {
      question: 'Can you help with compliance and security?',
      answer: 'Absolutely. We implement security best practices including VPC setup, IAM policies, encryption, compliance frameworks (SOC2, HIPAA, PCI-DSS), and security monitoring throughout your cloud infrastructure.'
    },
    {
      question: 'What is your approach to CI/CD implementation?',
      answer: 'We implement GitOps workflows with automated testing, security scanning, and deployment pipelines. Our CI/CD solutions include blue-green deployments, canary releases, and automatic rollback capabilities.'
    },
  ];

  const process = [
    { step: '01', title: 'Infrastructure Assessment', description: 'Analyzing current infrastructure and requirements' },
    { step: '02', title: 'Architecture Design', description: 'Designing scalable and secure cloud architecture' },
    { step: '03', title: 'Migration Planning', description: 'Creating detailed migration strategy and timeline' },
    { step: '04', title: 'Implementation', description: 'Deploying infrastructure and migrating applications' },
    { step: '05', title: 'Testing & Optimization', description: 'Performance testing and cost optimization' },
    { step: '06', title: 'Monitoring & Support', description: 'Setting up monitoring and ongoing support' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>
                  <h1 className={classes.heroTitle}>
                    Cloud DevOps & <span className={classes.gradient}>Infrastructure</span>
                  </h1>
                  <p className={classes.heroSubtitle}>
                    Scale your applications with modern cloud infrastructure, automated DevOps pipelines, and enterprise-grade reliability.
                    From migration to optimization, we handle your entire cloud journey.
                  </p>
                  <div className={classes.heroCTA}>
                    <button
                      className={classes.exploreButton}
                      onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Explore Services
                    </button>
                    <button
                      className={classes.quoteButton}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get A Quote
                    </button>
                  </div>
                  <div className={classes.heroStats}>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>50+</span>
                      <span className={classes.statLabel}>Cloud Migrations</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>99.9%</span>
                      <span className={classes.statLabel}>Uptime</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>40%</span>
                      <span className={classes.statLabel}>Cost Savings</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <img
                    src={cloudHeroImg}
                    alt="Cloud Infrastructure"
                    className={classes.phoneImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Cloud & <span className={classes.gradient}>DevOps Services</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Technologies We <span className={classes.gradient}>Master</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Industry-leading tools and platforms for modern cloud infrastructure
              </p>
            </div>
            <div className={classes.techGrid}>
              {technologies.map((tech, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{tech.icon}</div>
                  <h3 className={classes.techName}>{tech.name}</h3>
                  <p className={classes.techDescription}>{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className={classes.portfolioSection}>
          <div className={classes.portfolioContainer}>
            <div className={classes.portfolioHeader}>
              <h2 className={classes.portfolioTitle}>
                Our Cloud <span className={classes.gradient}>Projects</span>
              </h2>
            </div>

            <div className={classes.carousel}>
              <div className={classes.slideContainer}>
                <div
                  className={classes.slides}
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {cloudProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className={classes.slide}
                      style={{ background: project.bgGradient }}
                    >
                      <div className={classes.slideContent}>
                        <div className={classes.slideTop}>
                          <div className={classes.imageContainer}>
                            <img src={project.image} alt={project.title} />
                          </div>
                        </div>

                        <div className={classes.slideBottom}>
                          <div className={classes.slideHeader}>
                            <h3 className={classes.projectTitle}>{project.title}</h3>
                            <span className={classes.categoryBadge}>{project.category}</span>
                          </div>

                          <h4 className={classes.projectSubtitle}>{project.subtitle}</h4>

                          <p className={classes.projectDescription}>{project.description}</p>

                          <a href={project.link} className={classes.learnMoreBtn}>
                            <span>Learn More</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.navigation}>
                <button
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prevSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={nextSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <div className={classes.indicators}>
                {cloudProjects.map((_, index) => (
                  <button
                    key={index}
                    className={`${classes.indicator} ${index === currentSlide ? classes.active : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Cloud <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Proven methodology for successful cloud transformation and DevOps implementation
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Build Your <span className={contactClasses.gradient}>Cloud Infrastructure</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    Cloud Infrastructure Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your cloud infrastructure needs - current setup, migration requirements, scaling goals, and any specific challenges..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Scale Your <span className={classes.gradient}>Cloud Infrastructure</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's modernize your infrastructure with cloud-native solutions and DevOps best practices
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Your Cloud Journey</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default CloudDevOpsInfrastructure;